import { renderToStaticMarkup } from 'react-dom/server';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Box, styled } from '@mui/material';
import { RadioGroupV2 } from '@/components/@IntranetLibrary/Radio/RadioV2';
import { ApprovalFormTable } from '@/components/Approval/@components/ApprovalFormTable';
import {
  APPROVAL_SECTION_GAP_LG,
  InfoSectionTitle,
} from '@/components/Approval/@components/InfoSection';
import {
  PledgeBox,
  ResignationEmployeeTable,
} from '@/components/Approval/@components/Resignation';
import type { VehicleCostEmployee } from '@/utils/approval/vehicleCost';
import {
  isFirstAnniversary,
  toLongServiceLeaveData,
  type LongServiceLeaveData,
  type LongServiceLeaveFormState,
} from '@/utils/approval/longServiceLeave';

/** 레거시 서약서 문구 — 원문 그대로(번역 없음) */
export const LONG_SERVICE_PLEDGE_INTRO =
  '상기 본인은 회사의 장기근속포상제도에 따라 장기근속포상휴가 및 포상금 일체를(이하 “지원금”) 지급받음에 있어, 회사의 규정과 지침을 성실히 준수할 것을 서약합니다.';

export const LONG_SERVICE_PLEDGE_ITEMS = [
  '본인은 장기근속포상휴가를 회사가 정한 목적(재충전 및 장기근속 격려) 외의 용도로 사용하지 않겠습니다.',
  '회사가 정한 기준 및 절차를 위반하거나, 허위·부정한 방법으로 포상휴가 또는 지원금을 수령한 사실이 확인될 경우, 회사는 지원금 전액을 회수하고 징계조치를 취할 수 있음에 동의합니다.',
  '포상휴가를 사용한 후 2개월 이내 퇴사 시 본인은 수령한 지원금 전액을 회사에 즉시 반환하겠습니다.',
  '본 서약서는 회사의 인사규정 및 복리후생운영지침과 동일한 효력을 가지며, 이를 위반할 경우 인사상 불이익 및 징계 처분을 감수하겠습니다.',
];

export const LONG_SERVICE_PLEDGE_OUTRO =
  '본인은 위 사항을 충분히 이해하고, 회사의 장기근속포상휴가 제도를 기준에 맞게 성실히 이행할 것을 서약합니다.';

/** 서약서 본문 — 작성·조회 화면과 저장 HTML 공용 */
export const LongServicePledgeText = () => (
  <>
    {LONG_SERVICE_PLEDGE_INTRO}
    <ol style={{ margin: '15px 0', paddingLeft: 20, listStyle: 'decimal' }}>
      {LONG_SERVICE_PLEDGE_ITEMS.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ol>
    {LONG_SERVICE_PLEDGE_OUTRO}
  </>
);

export const LongServicePledgeBox = styled(PledgeBox)`
  & ol li {
    margin-top: 0;
  }
`;

const periodText = (data: LongServiceLeaveData, t: TFunction): string =>
  `${data.start_date} ~ ${data.end_date} (${t('{{count}}일', {
    count: Number(data.use_count),
  })})`;

type MarkupProps = { state: LongServiceLeaveFormState; t: TFunction };

/** 레거시 #APV_LONG_SERVICE_AWARD 마크업(id·class 동일) — 작성 시점 신청자 정보·선택값을 담아 둠 */
const LongServiceLeaveContentMarkup = ({ state, t }: MarkupProps) => {
  const { applicant } = state;
  const data = toLongServiceLeaveData(state);
  const hasPeriod = !isFirstAnniversary(state.serviceYears);
  return (
    <>
      <div className="form-group">
        <strong>{t('신청자 정보')}</strong>
        <div className="textarea mt-10">
          <ul className="list-bagic-line">
            <li>
              <div className="tit">{t('이름')}</div>
              <div id="emp_name">{applicant.name}</div>
            </li>
            <li>
              <div className="tit">{t('사번')}</div>
              <div>
                <span id="emp_no" data-value={applicant.employeeNumber}>
                  {applicant.employeeNumber}
                </span>
              </div>
            </li>
            <li>
              <div className="tit">{t('소속')}</div>
              <div>{applicant.affiliation}</div>
            </li>
            <li>
              <div className="tit">{t('입사일')}</div>
              <div>{applicant.hireDate}</div>
            </li>
          </ul>
        </div>
      </div>
      <div className="form-group mt-40">
        <strong>{t('신청 정보')}</strong>
        <div className="textarea mt-10">
          <ul className="list-bagic-line">
            <li>
              <div className="tit">{t('포상 내용')}</div>
              <div id="service_years_display">{data.service_years}</div>
            </li>
            {hasPeriod && (
              <li id="period_row">
                <div className="tit">{t('휴가 기간')}</div>
                <div id="period_display">{periodText(data, t)}</div>
              </li>
            )}
          </ul>
        </div>
      </div>
      <div className="form-group mt-40">
        <label className="label fw-bold">
          {t('장기근속 포상휴가 사용 서약')}
        </label>
        <div className="mt-10">
          <LongServicePledgeText />
        </div>
        <div id="pledgeAgree" className="mt-15">
          <div className="d-flex justify-content-end">
            <label className="mr-15">
              <input
                type="radio"
                name="pledgeAgree"
                value="Y"
                disabled
                defaultChecked={state.pledgeAgree === 'Y'}
              />{' '}
              {t('동의함')}
            </label>
            <label>
              <input
                type="radio"
                name="pledgeAgree"
                value="N"
                disabled
                defaultChecked={state.pledgeAgree === 'N'}
              />{' '}
              {t('동의하지 않음')}
            </label>
          </div>
        </div>
      </div>
    </>
  );
};

/** attributes.content(APV_SUB.CL01) */
export const buildLongServiceLeaveContentHtml = (
  state: LongServiceLeaveFormState,
  t: TFunction,
): string =>
  renderToStaticMarkup(<LongServiceLeaveContentMarkup state={state} t={t} />);

type ApplicantSnapshot = Pick<
  VehicleCostEmployee,
  'name' | 'employeeNumber' | 'affiliation' | 'enterDate'
>;

/** 저장 content 첫 목록(신청자 정보)의 값 — 레거시·V2 문서 모두 같은 마크업 */
export const parseApplicantFromContent = (
  content: string | null,
): ApplicantSnapshot | null => {
  if (!content || typeof DOMParser === 'undefined') return null;
  const doc = new DOMParser().parseFromString(content, 'text/html');
  const items = doc.querySelectorAll('ul.list-bagic-line')[0]?.children;
  if (!items || items.length < 4) return null;
  const valueOf = (i: number) =>
    items[i]?.querySelector(':scope > div:not(.tit)')?.textContent?.trim() ??
    '';
  const name = valueOf(0);
  const employeeNumber = valueOf(1);
  if (!name && !employeeNumber) return null;
  return {
    name,
    employeeNumber,
    affiliation: valueOf(2).replace(/\s+/g, ' '),
    enterDate: valueOf(3),
  };
};

const ViewRoot = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${APPROVAL_SECTION_GAP_LG}px;
`;

const viewRadioSx = {
  justifyContent: 'flex-end',
  mt: '15px',
  '& .MuiFormControlLabel-root.Mui-disabled .MuiFormControlLabel-label.Mui-disabled':
    {
      color: 'var(--dk-text, #1A1A1A)',
      fontSize: 14,
      lineHeight: '20px',
    },
};

type ViewProps = {
  applicant: ApplicantSnapshot;
  data: LongServiceLeaveData;
};

/** 조회 — 레거시 appr_view_forms_hr_longservice_leave. 기간이 비어 있으면([1주년]) 기간 칸 없음 */
export const LongServiceLeaveView = ({ applicant, data }: ViewProps) => {
  const { t } = useTranslation();
  const hasPeriod = !!(data.start_date && data.end_date);
  return (
    <ViewRoot>
      <Box>
        <InfoSectionTitle sx={{ mb: 1 }}>{t('신청자 정보')}</InfoSectionTitle>
        <ResignationEmployeeTable employee={applicant} />
      </Box>
      <Box>
        <InfoSectionTitle sx={{ mb: 1 }}>{t('신청 정보')}</InfoSectionTitle>
        <ApprovalFormTable>
          <tbody>
            <tr>
              <th>{t('포상 내용')}</th>
              {hasPeriod && <th>{t('휴가 기간')}</th>}
            </tr>
            <tr>
              <td className="text-center">{data.service_years || '-'}</td>
              {hasPeriod && (
                <td className="text-center">{periodText(data, t)}</td>
              )}
            </tr>
          </tbody>
        </ApprovalFormTable>
      </Box>
      <Box>
        <InfoSectionTitle sx={{ mb: 1 }}>
          {t('장기근속 포상휴가 사용 서약')}
        </InfoSectionTitle>
        <LongServicePledgeBox>
          <LongServicePledgeText />
        </LongServicePledgeBox>
        <RadioGroupV2
          row
          disabled
          value={data.pledge_agree}
          options={[
            { value: 'Y', label: t('동의함') },
            { value: 'N', label: t('동의하지 않음') },
          ]}
          sx={viewRadioSx}
        />
      </Box>
    </ViewRoot>
  );
};
